<template>
  <div ref="containerRef">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

const props = defineProps({
  options: { type: Object, default: () => ({}) },
});

const containerRef = ref(null);
let mutationObserver: MutationObserver | null = null;
let FancyboxApi: any = null;

const ensureFancybox = async () => {
  if (FancyboxApi) {
    return FancyboxApi;
  }

  const pkg: any = await import("@fancyapps/ui");
  FancyboxApi = pkg.Fancybox ?? pkg.default?.Fancybox ?? pkg.default ?? pkg;
  return FancyboxApi;
};

const bindFancybox = async () => {
  if (!containerRef.value) {
    return;
  }

  const Fancybox = await ensureFancybox();
  if (!Fancybox || typeof Fancybox.bind !== 'function') {
    return;
  }

  try {
    Fancybox.unbind(containerRef.value);
    Fancybox.bind(containerRef.value, "[data-fancybox]", {
      ...(props.options || {}),
    });
  } catch (err) {
    console.warn("Fancybox bind error:", err);
  }
};

onMounted(async () => {
  await nextTick();
  void bindFancybox();

  if (containerRef.value && typeof window !== "undefined") {
    mutationObserver = new MutationObserver(() => {
      void bindFancybox();
    });

    mutationObserver.observe(containerRef.value, {
      childList: true,
      subtree: true,
    });
  }
});

watch(
  () => props.options,
  () => {
    void bindFancybox();
  },
  { deep: true }
);

onBeforeUnmount(async () => {
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }

  if (containerRef.value) {
    const Fancybox = await ensureFancybox();
    if (Fancybox) {
      Fancybox.unbind(containerRef.value);
      Fancybox.close();
    }
  }
});
</script>
